/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Bell, Trophy, Calendar, ShoppingBag, Gift, Megaphone, X, ExternalLink } from "lucide-react";
import { useCustomerAuth } from "../../lib/useCustomerAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  bell: Bell, trophy: Trophy, calendar: Calendar,
  shop: ShoppingBag, gift: Gift, megaphone: Megaphone,
};

/**
 * Cloche notif réutilisable. Va chercher les notifs in-app du user courant.
 * Source d'auth : `useCustomerAuth` context (réagit au login/logout instantanément).
 * Fallback : kap_token (admin) si pas de customer.
 *
 * 🔔 Effet "shake + ping" : déclenché 1 fois quand `unread` augmente (nouvelle notif).
 *    Pulse léger permanent quand `unread > 0` pour attirer l'œil.
 *
 * 🍞 Toast slide-in : à chaque nouvelle notif détectée, on affiche un toast
 *    en haut à droite (queue de 3 max) qui auto-dismiss en 6s.
 */
export default function NotificationsBell({ className = "" }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [shake, setShake] = useState(false);
  const [toastQueue, setToastQueue] = useState([]); // [{id, ...notif}]
  const prevUnreadRef = useRef(0);
  const prevIdsRef = useRef(new Set());
  const isFirstFetchRef = useRef(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Auth réactif (re-render automatique au login/logout)
  const { isAuthenticated } = useCustomerAuth();

  // Token courant — recalculé à chaque changement d'état d'auth
  const token = (() => {
    try {
      return localStorage.getItem("msc_customer_token") || localStorage.getItem("kap_token") || "";
    } catch { return ""; }
  })();

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetch = async () => {
    if (!token) { setItems([]); setUnread(0); return; }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/notifications/me`, { headers });
      const newUnread = data?.unread_count || 0;
      const newItems = data?.items || [];
      setItems(newItems);
      setUnread(newUnread);
      // Trigger shake + slide-in toasts si nouvelle notif (mais pas au tout 1er chargement)
      if (!isFirstFetchRef.current && newUnread > prevUnreadRef.current) {
        setShake(true);
        setTimeout(() => setShake(false), 1400);
        // Détecte les nouvelles notifs (items présents maintenant mais pas avant)
        const fresh = newItems.filter((n) => !prevIdsRef.current.has(n.id) && !n.is_read);
        if (fresh.length > 0) {
          // On ajoute jusqu'à 3 toasts max, les plus récents
          setToastQueue((q) => [...fresh.slice(0, 3), ...q].slice(0, 3));
        }
      }
      prevIdsRef.current = new Set(newItems.map((n) => n.id));
      prevUnreadRef.current = newUnread;
      isFirstFetchRef.current = false;
    } catch (e) {
      // silent
    } finally { setLoading(false); }
  };

  // Auto-dismiss des toasts après 6s
  useEffect(() => {
    if (toastQueue.length === 0) return;
    const timers = toastQueue.map((t) =>
      setTimeout(() => {
        setToastQueue((q) => q.filter((x) => x.id !== t.id));
      }, 6000)
    );
    return () => timers.forEach(clearTimeout);
  }, [toastQueue.length]);

  const dismissToast = (id) => setToastQueue((q) => q.filter((x) => x.id !== id));

  useEffect(() => {
    isFirstFetchRef.current = true; // reset au changement d'auth
    fetch();
    // Poll toutes les 20s (au lieu de 60s) pour réactivité accrue
    const t = setInterval(fetch, 20000);
    return () => clearInterval(t);
  }, [token, isAuthenticated]);

  const markRead = async (nid) => {
    try {
      await axios.post(`${API}/notifications/${nid}/read`, {}, { headers });
      setItems((prev) => prev.map((n) => n.id === nid ? { ...n, is_read: true } : n));
      setUnread((u) => Math.max(0, u - 1));
    } catch { /* silent */ }
  };

  /**
   * Handler pour le bouton CTA d'une notification :
   *  - marque la notif comme lue
   *  - ferme la cloche
   *  - navigue vers cta_url (interne) ou ouvre dans un nouvel onglet (externe)
   *  - si l'URL est la page courante, scroll vers le haut au lieu de no-op
   */
  const handleCtaClick = (e, n) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!n?.cta_url) return;
    if (!n.is_read) markRead(n.id);
    setOpen(false);
    const url = String(n.cta_url).trim();
    // URL externe (http/https/mailto/tel) → nouvel onglet
    if (/^(https?:|mailto:|tel:)/i.test(url)) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    // URL interne — extraire path + hash
    const [path, hash] = url.split("#");
    if (path && path !== location.pathname) {
      navigate(hash ? `${path}#${hash}` : path);
    } else {
      // Même page : scroll vers le hash si présent, sinon vers le haut
      if (hash) {
        setTimeout(() => {
          const el = document.getElementById(hash);
          el?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 50);
      } else {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  };

  if (!token) return null;

  return (
    <div className={`relative ${className}`} data-testid="notif-bell-wrap">
      {/* SLIDE-IN TOASTS — empilés en haut à droite, sous le header (z fixe) */}
      <NotifToastStack toasts={toastQueue} onDismiss={dismissToast} onMarkRead={markRead} onCta={(n) => handleCtaClick(null, n)}/>

      {/* PING RINGS — burst quand nouvelle notif arrive */}
      <AnimatePresence>
        {shake && (
          <>
            <motion.span
              key="ping-1"
              initial={{ scale: 0.8, opacity: 0.7 }}
              animate={{ scale: 2.2, opacity: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.2, ease: "easeOut" }}
              className="pointer-events-none absolute inset-0 m-auto w-10 h-10 rounded-full bg-pink-500/40"
              data-testid="notif-ping-1"
            />
            <motion.span
              key="ping-2"
              initial={{ scale: 0.8, opacity: 0.5 }}
              animate={{ scale: 1.7, opacity: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.0, ease: "easeOut", delay: 0.2 }}
              className="pointer-events-none absolute inset-0 m-auto w-10 h-10 rounded-full bg-pink-400/30"
            />
          </>
        )}
      </AnimatePresence>

      {/* IDLE PULSE — léger glow permanent quand il y a des unread */}
      {unread > 0 && !shake && (
        <span
          className="pointer-events-none absolute inset-0 m-auto w-10 h-10 rounded-full bg-pink-500/0 animate-pulse"
          style={{ boxShadow: "0 0 0 0 rgba(236,31,124,0.5)", animation: "iphbBellPulse 2s ease-in-out infinite" }}
        />
      )}

      <motion.button
        type="button"
        onClick={() => { setOpen(!open); if (!open) fetch(); }}
        animate={shake ? { rotate: [0, -18, 14, -10, 6, -3, 0], scale: [1, 1.08, 1.08, 1.04, 1] } : { rotate: 0, scale: 1 }}
        transition={shake ? { duration: 0.9, ease: "easeInOut" } : { duration: 0.2 }}
        className="relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 backdrop-blur hover:bg-white/20 border border-white/20 transition"
        data-testid="notif-bell"
      >
        <Bell className="w-5 h-5 text-white"/>
        {unread > 0 && (
          <motion.span
            animate={shake ? { scale: [1, 1.3, 1] } : { scale: 1 }}
            transition={{ duration: 0.5 }}
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-pink-500 text-white text-[10px] font-black flex items-center justify-center shadow-lg"
            data-testid="notif-badge"
          >
            {unread > 9 ? "9+" : unread}
          </motion.span>
        )}
      </motion.button>
      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)}/>
            <motion.div
              initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              className="fixed sm:absolute left-2 right-2 top-[68px] sm:left-auto sm:right-0 sm:top-12 z-50 sm:w-[380px] bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
              data-testid="notif-drawer"
            >
              <div className="p-4 bg-gradient-to-br from-violet-700 to-pink-600 text-white flex items-center justify-between">
                <h3 className="font-black uppercase text-sm tracking-wider">Notifications</h3>
                <button onClick={() => setOpen(false)} className="w-7 h-7 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center"><X size={14}/></button>
              </div>
              <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-100">
                {loading ? (
                  <div className="p-6 text-center text-sm text-slate-500">Chargement...</div>
                ) : items.length === 0 ? (
                  <div className="p-8 text-center">
                    <Bell className="w-10 h-10 mx-auto text-slate-300 mb-2"/>
                    <p className="text-sm text-slate-500">Aucune notification pour l'instant</p>
                  </div>
                ) : items.map((n) => {
                  const Icon = ICON_MAP[n.icon] || Bell;
                  return (
                    <div
                      key={n.id}
                      className={`p-3 hover:bg-slate-50 transition cursor-pointer ${!n.is_read ? "bg-pink-50/40" : ""}`}
                      onClick={() => { if (!n.is_read) markRead(n.id); }}
                      data-testid={`notif-item-${n.id}`}
                    >
                      <div className="flex gap-3">
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm" style={{ background: n.color || "#EC1F7C" }}>
                          <Icon className="w-4 h-4 text-white"/>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="font-bold text-sm text-slate-900 truncate">{n.title}</h4>
                            {!n.is_read && <span className="w-2 h-2 rounded-full bg-pink-500 flex-shrink-0"/>}
                          </div>
                          <p className="text-xs text-slate-600 mt-0.5 leading-snug line-clamp-3">{n.body}</p>
                          {n.cta_label && n.cta_url && (
                            <button
                              type="button"
                              onClick={(e) => handleCtaClick(e, n)}
                              data-testid={`notif-cta-${n.id}`}
                              className="inline-flex items-center gap-1 text-xs font-bold text-pink-600 hover:text-pink-700 mt-1.5"
                            >
                              {n.cta_label}<ExternalLink className="w-3 h-3"/>
                            </button>
                          )}
                          <div className="text-[10px] text-slate-400 mt-1.5 uppercase tracking-wider">{formatDate(n.sent_at)}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "à l'instant";
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
    return d.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  } catch { return ""; }
}

/**
 * Pile de toasts slide-in (haut-droite, sous header).
 * Affichés via portal sur document.body pour échapper au stacking parent.
 */
function NotifToastStack({ toasts, onDismiss, onMarkRead, onCta }) {
  if (typeof document === "undefined") return null;
  return createPortal(
    <div
      className="fixed z-[90] top-[88px] right-3 sm:right-5 flex flex-col gap-2 pointer-events-none w-[calc(100vw-1.5rem)] sm:w-[360px] max-w-[360px]"
      data-testid="notif-toast-stack"
      aria-live="polite"
    >
      <AnimatePresence>
        {toasts.map((n) => {
          const Icon = ICON_MAP[n.icon] || Bell;
          const accent = n.color || "#EC1F7C";
          return (
            <motion.div
              key={n.id}
              initial={{ x: 360, opacity: 0, scale: 0.96 }}
              animate={{ x: 0, opacity: 1, scale: 1 }}
              exit={{ x: 360, opacity: 0, scale: 0.96, transition: { duration: 0.25 } }}
              transition={{ type: "spring", damping: 22, stiffness: 260 }}
              className="pointer-events-auto rounded-xl bg-white shadow-2xl border border-slate-100 overflow-hidden"
              data-testid={`notif-toast-${n.id}`}
            >
              <div className="h-1" style={{ background: accent }}/>
              <div className="p-3 flex gap-3">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm"
                  style={{ background: accent }}
                >
                  <Icon className="w-5 h-5 text-white"/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2">
                    <h4 className="font-black text-[13px] text-slate-900 truncate uppercase tracking-wide">{n.title}</h4>
                    <button
                      type="button"
                      onClick={() => onDismiss(n.id)}
                      className="ml-auto -mt-0.5 -mr-1 w-6 h-6 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700 flex-shrink-0 transition"
                      aria-label="Fermer"
                      data-testid={`notif-toast-close-${n.id}`}
                    >
                      <X size={12}/>
                    </button>
                  </div>
                  <p className="text-[12px] text-slate-600 leading-snug line-clamp-2 mt-0.5">{n.body}</p>
                  {n.cta_label && n.cta_url && (
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onMarkRead(n.id); onDismiss(n.id); onCta?.(n); }}
                      className="inline-flex items-center gap-1 text-[11px] font-black uppercase tracking-wider mt-2 px-2.5 py-1 rounded-full text-white transition hover:scale-105"
                      style={{ background: accent }}
                      data-testid={`notif-toast-cta-${n.id}`}
                    >
                      {n.cta_label}<ExternalLink className="w-3 h-3"/>
                    </button>
                  )}
                </div>
              </div>
              <motion.div
                className="h-0.5"
                style={{ background: accent }}
                initial={{ width: "100%" }}
                animate={{ width: "0%" }}
                transition={{ duration: 6, ease: "linear" }}
              />
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>,
    document.body
  );
}

